import React from 'react';
import classNames from 'classnames';

import { withErrorBoundary } from '../../errors';
import { DrawdownDisclaimer, BenchmarksDisclaimer, InstrumentCustomDisclaimer, ShortHistoryPortfoliosDisclaimer } from '../../disclaimers/comparison';
import LineChart, { LineChartOptions } from '../LineChart';
import SeriesLegend, { SeriesLegendProps } from '../SeriesLegend/SeriesLegend';

import { getDefaultChartOptions, getMergedHighchartsOptions } from '@/views/containers/comparison/Charts/utils/drawdown-chart-helpers';
import { COMPARISON_ITEM_TYPES, TIME_PERIODS } from '@/utils/constants';
import { ROLLING_INTERVALS } from '@/utils/portfolios';
import ComparisonItem from '@/types/comparison-item';
import { DISCLAIMER_TAG } from '@/utils/disclaimers/constants';
import { getDateRangeFromQuickRange } from '@/utils/disclaimers/comparison';
import { SeriesOptionsType } from 'highcharts';
import Nullable from '@/types/nullable';

type Props = {
  data: SeriesOptionsType[];
  chartOptions: LineChartOptions;
  customDisclaimer?: boolean;
  dark?: boolean;
  detailedDisclaimer?: boolean;
  emptyStateText?: string;
  interval: ROLLING_INTERVALS;
  items: ComparisonItem[];
  seriesLegendProps?: SeriesLegendProps;
  shortHistory?: boolean;
  startAndEndDisclaimerDates?: {
    startDate: Date | null;
    endDate: Date | null;
  };
  startDate?: Nullable<Date>;
  endDate?: Date;
  dateQuickRange?: Nullable<TIME_PERIODS>;
};

const DrawdownAnalysisChart: React.FC<Props> = ({
  data,
  chartOptions,
  customDisclaimer,
  dark,
  detailedDisclaimer,
  emptyStateText,
  interval,
  items,
  seriesLegendProps,
  shortHistory,
  startAndEndDisclaimerDates,
  startDate,
  endDate,
  dateQuickRange,
}) => {
  const showFooterDisclaimer = data.length > 0;
  const comparisonHasBenchmarks = items.some(item => item.type === COMPARISON_ITEM_TYPES.BENCHMARK);
  const defaultOptions = getDefaultChartOptions();
  const mergedOptions = getMergedHighchartsOptions(defaultOptions, chartOptions, data);

  const { startDate: calculatedStartDate, endDate: calculatedEndDate } = getDateRangeFromQuickRange(dateQuickRange, startDate, endDate, items, true);

  return (
    <div className={classNames('drawdown-analysis chart', { dark })}>
      <LineChart dark={dark} data={data} chartOptions={mergedOptions} emptyStateText={emptyStateText} />
      <SeriesLegend {...seriesLegendProps} />
      {showFooterDisclaimer && (
        <div className="chart-disclaimer">
          <DrawdownDisclaimer
            shorthand={!detailedDisclaimer}
            items={items}
            series={data.reduce((acc, serie) => acc.concat(serie.data), [])}
            interval={interval}
            startAndEndDisclaimerDates={startAndEndDisclaimerDates}
          />
          {comparisonHasBenchmarks && <BenchmarksDisclaimer items={items} />}
          {customDisclaimer && <InstrumentCustomDisclaimer disclaimerTags={[DISCLAIMER_TAG.PROPOSAL]} items={items} />}
          {shortHistory && <ShortHistoryPortfoliosDisclaimer items={items} startDate={calculatedStartDate} endDate={calculatedEndDate} />}
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(DrawdownAnalysisChart);
